/* eslint-disable no-unused-vars */
import { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Formik } from 'formik';
import { Button } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { openPrintPreview } from '../../components/Print';

// Components
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';

// Local components

// Custom hooks
import {
  usePatientPlanData,
  usePaginationParser, useServiceDetails,
  useDoctorRoundModal
} from './hooks';

// Utils and constants
import { medicineInit } from './utils/constants';
import { validationSchema } from './validation/schema';
import { useFormSubmission } from './hooks/useFormSubmission';
import { calculateAge } from '../../utils/functions';
import {
  transformDietPlansFromAPI, transformMedicinesFromAPI, transformDoctorRoundFormFromAPI
} from './utils/dataTransformers';

// Local components
import { NameHeading } from './components/NameHeading';
import { PatientPlanSelector } from './components/PatientPlanSelector';
import { PaginationControls } from './components/PaginationControls';
import { TreatmentPlanTable } from './components/TreatmentPlanTable';
import { DoctorRoundModal } from './components/DoctorRoundModal';
import { DietPlanSectionWrapper } from './components/DietPlanSectionWrapper';
import { ActionGetPatientPlanList } from '../../store/Actions/AppConfigAction';
import { useParams } from 'react-router-dom';
import { NotesSection } from './components/NotesSection';
import PatientCCPaperView from '../../components/PatientCCPaperView/PatientCCPaperView';
import { formatTime } from '../../utils/helpers/dateHelpers';

/**
 * Indoor Casepaper - Main Component
 * Manages patient treatment plans, doctor rounds, and medicines
 */
const IndorCase = () => {
  // Redux state
  const { loader, patientPlanList } = useSelector(state => state?.appConfig || {});
  const patientDetail = useSelector(state => state?.patient?.CcPaperDetail);
  const dispatch = useDispatch();

  // Ref to store current form values for printing
  const formValuesRef = useRef(null);
  // Ref to track if we've already fetched patientPlanList
  const hasFetchedPlanListRef = useRef(false);

  // URL parameters
  const url = typeof window !== 'undefined' ? new URL(window.location.href) : null;
  const searchParams = url?.searchParams;
  const treatmentSlotId = searchParams?.get('slotId');
  const solutionSlotId = searchParams?.get('solutionslotId');
  const serviceSlotId = searchParams?.get('serviceSlotId');
  const roomName = searchParams?.get('roomName');
  const roomType = searchParams?.get('roomType');
  const { patientPlanId } = useParams();
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(0);

  // Fetch patient plan list only once per patientPlanId
  useEffect(() => {
    if (patientPlanId && !hasFetchedPlanListRef.current) {
      hasFetchedPlanListRef.current = true;
      dispatch(ActionGetPatientPlanList({ Query: `/${patientPlanId}` }));
    }
  }, [dispatch, patientPlanId]);

  // Reset ref when patientPlanId changes
  useEffect(() => {
    hasFetchedPlanListRef.current = false;
  }, [patientPlanId]);

  // NOTE: PatientCCPaperView handles fetching patient detail data with proper caching
  // No need to call ActionGetCCPaperDetail here

  // Custom hooks
  const {
    currentPage,
    setCurrentPage,
    loading,
    error,
    setError,
  } = usePatientPlanData(treatmentSlotId, solutionSlotId);

  const {
    paginationData,
    patientPlans,
    paginationInfo,
    hasMultiplePlans,
  } = usePaginationParser(patientPlanList);



  const {
    patientPlanData,
    currentIndex,
    processObjectsInPairs,
    handlePlanChange,
  } = useServiceDetails(patientPlanList, selectedPlanIndex);

  const {
    isModel,
    SetModel,
    autoDetectedRoundType,
    getAutoDetectedRoundType,
  } = useDoctorRoundModal();

  // Get the currently selected plan from the list
  const currentPlan = useMemo(() => {
    const paginationData = Array.isArray(patientPlanList)
      ? patientPlanList
      : (patientPlanList?.data || patientPlanList || {});

    const patientPlans = paginationData?.docs || (paginationData?._id ? [paginationData] : []);
    return patientPlans[selectedPlanIndex] || patientPlans[0] || {};
  }, [patientPlanList, selectedPlanIndex]);

  const { handleSubmit: onFormSubmit, handleSubmitSpecificRound } = useFormSubmission(
    patientPlanId,
    patientPlanData,
    setError,
    currentPlan
  );

  // Handler for printing the full report
  const handleCombinedPrint = () => {
    const displayData = patientDetail || currentPlan?.customerId || {
      name: currentPlan?.customerName,
      email: currentPlan?.customerEmail,
      age: currentPlan?.customerAge,
      phoneNo: currentPlan?.customerPhoneNo,
      gender: currentPlan?.customerGender,
      birthDate: currentPlan?.customerBirthdate,
      registrationNo: currentPlan?.registrationNo,
    };

    // 1. Patient & Treatment Overview Sections
    const patientSections = [
      {
        title: 'Patient Details',
        fields: [
          { label: 'Registration No', value: displayData?.registrationNo || '-' },
          { label: 'Name', value: displayData?.name || '-' },
          { label: 'Email', value: displayData?.email || '-' },
          { label: 'Phone No', value: displayData?.phoneNo || '-' },
          { label: 'Gender', value: displayData?.gender || '-' },
          { label: 'Age', value: displayData?.birthDate ? calculateAge(displayData.birthDate) : displayData?.age || '-' },
          { label: 'Room Name', value: roomName || '-' },
          { label: 'Room Type', value: roomType || '-' },
          { label: 'Birth Date', value: displayData?.birthDate ? moment(displayData.birthDate).format('DD-MM-YYYY') : '-' },
        ],
      },
      {
        title: 'Treatment Overview',
        fields: [
          { label: 'Treatment Name', value: currentPlan?.treatmentName || '-' },
          { label: 'Total Days', value: currentPlan?.additionalInfo?.totalDays || '-' },
          { label: 'CheckIn Date', value: currentPlan?.additionalInfo?.startDate || '-' },
          { label: 'CheckOut Date', value: currentPlan?.additionalInfo?.endDate || '-' },
        ],
      }
    ];

    // 2. Treatment Schedule Chart Sections
    const dataSource = patientPlanData?.dataSource || [];
    const allDates = patientPlanData?.allDates || [];

    const scheduleSections = dataSource.map((item, index) => ({
      title: `Schedule: ${item.serviceName} (${formatTime(item.startTime)} - ${formatTime(item.endTime)})`,
      fields: allDates.map(date => ({
        label: moment(date).format('DD MMM (ddd)'),
        value: item[date]?.medicineValue || 'Not Started'
      }))
    }));

    // 3. Clinical Notes
    const notesSection = formValuesRef.current?.notes ? [
      {
        title: 'Clinical Notes',
        fields: [
          { label: 'Observations', value: formValuesRef.current.notes }
        ]
      }
    ] : [];

    // Combine everything
    openPrintPreview(displayData, 'Full Indoor Casepaper Report', {
      sections: [...patientSections, ...scheduleSections, ...notesSection],
      title: `Medical Record: ${displayData?.name || ''}`
    });
  };


  // Today's date
  const todayDate = moment().format('YYYY-MM-DD');

  // Transform medicines from API format to form format
  // Using useMemo to ensure this recalculates when currentPlan.medicine changes
  const initialMedicines = useMemo(() => {
    if (currentPlan?.medicine && Object.keys(currentPlan.medicine).length > 0) {
      const transformed = transformMedicinesFromAPI(currentPlan.medicine);
      return transformed;
    }
    return [{ ...medicineInit, date: todayDate }];
  }, [currentPlan?.medicine, todayDate]);

  // Transform diet plans from API format to form format
  const initialDietPlans = useMemo(() => {
    if (currentPlan?.dietPlan && Object.keys(currentPlan.dietPlan).length > 0) {
      const transformed = transformDietPlansFromAPI(currentPlan.dietPlan);
      return transformed;
    }
    return [{ date: todayDate, _id: '', dietName: '' }];
  }, [currentPlan?.dietPlan, todayDate]);

  console.log({ patientPlanList });

  return (
    <>
      <Header />
      <Sidebar />
      <div className="page-wrapper">
        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show m-3" role="alert">
            <strong>Error:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label="Close"
            />
          </div>
        )}

        {/* Loading Indicator */}
        {loading && (
          <div className="alert alert-info m-3 d-flex align-items-center" role="alert">
            <div className="spinner-border spinner-border-sm me-2" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <strong>Loading patient plans...</strong>
          </div>
        )}

        <Formik
          enableReinitialize
          initialValues={{
            notes: currentPlan?.note || '',
            dietPlanNote: currentPlan?.dietPlanNote || '',
            medicines: initialMedicines,
            dietPlans: initialDietPlans,
            doctorRoundForm: transformDoctorRoundFormFromAPI(currentPlan?.doctorRoundForm),
          }}
          validationSchema={validationSchema}
          onSubmit={onFormSubmit}
        >
          {(formikProps) => {
            // Store form values in ref for printing
            formValuesRef.current = formikProps.values;

            return (
              <div className="content">
                <div className="card-box profile-header mb-5" id="printableArea">
                  <div className="row">
                    {/* Header with Global Print */}
                    <div className="col-12 d-flex justify-content-between align-items-center my-4 px-3">
                      <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                        <NameHeading />
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          type="primary"
                          icon={<PrinterOutlined />}
                          onClick={handleCombinedPrint}
                          style={{ height: '40px', borderRadius: '8px', background: '#52c41a', borderColor: '#52c41a' }}
                        >
                          Print Full Report
                        </Button>
                        <Button
                          type="primary"
                          onClick={formikProps.handleSubmit}
                          loading={formikProps.isSubmitting}
                          disabled={formikProps.isSubmitting}
                          style={{ height: '40px', borderRadius: '8px' }}
                        >
                          {formikProps.isSubmitting ? 'Saving...' : 'Save Plan'}
                        </Button>
                      </div>
                    </div>

                    {/* Patient Plan Selector */}
                    {hasMultiplePlans && (
                      <PatientPlanSelector
                        patientPlans={patientPlans}
                        selectedPlanIndex={selectedPlanIndex}
                        setSelectedPlanIndex={setSelectedPlanIndex}
                      />
                    )}

                    {/* Pagination Controls */}
                    {paginationInfo.totalPages > 1 && (
                      <PaginationControls
                        paginationInfo={paginationInfo}
                        loading={loading}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        setSelectedPlanIndex={setSelectedPlanIndex}
                      />
                    )}



                    {/* Patient Information */}
                    <PatientCCPaperView userId={currentPlan?.customerId} extraInfo={{ "Room Name": roomName, "Room Type": roomType }} />

                    {/* Treatment Plan Section Header */}
                    <div className="col-12 mt-4 mb-2 px-3">
                      <h4 className="mb-0" style={{ fontWeight: '600', color: '#764ba2' }}>Medicine Schedule</h4>
                    </div>

                    {/* Treatment Plan Table */}
                    <TreatmentPlanTable
                      patientPlanData={patientPlanData}
                      currentIndex={currentIndex}
                      processObjectsInPairs={processObjectsInPairs}
                      handlePlanChange={handlePlanChange}
                      SetModel={SetModel}
                      loading={loading}
                      roomName={roomName}
                      roomType={roomType}
                      formikProps={formikProps}
                      treatmentName={currentPlan?.treatmentName}
                      patientDetail={patientDetail}
                      patientPlanList={currentPlan}
                    />

                    {/* Medicine Section Header with Print */}


                    {/* Diet Plan Section */}
                    {!serviceSlotId && <DietPlanSectionWrapper
                      formikProps={formikProps}
                      todayDate={todayDate}
                      patientPlanData={patientPlanData}
                    />}

                    {/* notess Section */}
                    <NotesSection formikProps={formikProps} />

                    {/* Submit Button */}
                    <div className="row mt-4">
                      <div className="col-12 text-center">
                        <Button
                          type="primary"
                          size="large"
                          onClick={formikProps.handleSubmit}
                          loading={formikProps.isSubmitting}
                          disabled={formikProps.isSubmitting}
                        >
                          {formikProps.isSubmitting ? 'Saving...' : 'Save Patient Plan'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Doctor Round Modal */}
                <DoctorRoundModal
                  isModel={isModel}
                  SetModel={SetModel}
                  autoDetectedRoundType={autoDetectedRoundType}
                  getAutoDetectedRoundType={getAutoDetectedRoundType}
                  formikProps={formikProps}
                  patientPlanList={currentPlan}
                  patientDetail={patientDetail}
                  handleSubmitSpecificRound={handleSubmitSpecificRound}
                />
              </div>
            );
          }}
        </Formik >
      </div >
    </>
  );
};

export default IndorCase;
